import React from "react";
import type { DocType, ItemType } from "../../../clients/graphqlTypes.js";
import { TreeViewMode } from "../../trees-virtual/types.js";
import { CreateItemInput } from "../doc-items/CreateItemInput.js";
import type {
  CreateItemArgs,
  CreateItemByTypeArgs,
  CreateItemFromTemplateArgs,
  CreateItemsFromTemplatesArgs,
  CreateItemsWithProductsArgs,
} from "../doc-items/hooks/useCreateItem.js";
import type { DocDataGrid_ItemFragment } from "./DocDataGrid.generated.js";
import type {
  DocDataGridInputComponent,
  DocDataGridInputComponentProps,
} from "./DocDataGrid.js";

interface CreateDocDataGridInputCellOptions {
  viewMode: TreeViewMode;
  doc: {
    id: string;
    docType: DocType;
    templateIsDraft: boolean;
  };
  documentItemTypes: ItemType[];
  createItem: (args: CreateItemArgs) => Promise<{ id: string }>;
  createItemByType: (args: CreateItemByTypeArgs) => Promise<{ id: string }>;
  createItemFromTemplate:
    | ((
        args: CreateItemFromTemplateArgs
      ) => Promise<{ id: string } | null | undefined>)
    | null;
  createItemsFromTemplates:
    | ((
        args: CreateItemsFromTemplatesArgs
      ) => Promise<Array<{ id: string }> | null | undefined>)
    | null;
  createItemsWithProducts:
    | ((
        args: CreateItemsWithProductsArgs
      ) => Promise<Array<{ id: string }> | null | undefined>)
    | null;
  pasteItem: ((docId: string, parentItemId: string) => Promise<void>) | null;
  isAllowedToPasteItem: (item: DocDataGrid_ItemFragment) => boolean;
}

export function createDocDataGridInputCell<T extends DocDataGrid_ItemFragment>({
  viewMode,
  doc,
  documentItemTypes,
  createItem,
  createItemByType,
  createItemFromTemplate,
  createItemsFromTemplates,
  createItemsWithProducts,
  pasteItem,
  isAllowedToPasteItem,
}: CreateDocDataGridInputCellOptions): DocDataGridInputComponent<T> {
  const DocDataGridInputCell = ({
    id,
    onBeforeItemCreate,
    onAfterItemCreated,
    parentItem,
    isRootItem,
    itemChildren,
    nodeRank,
  }: DocDataGridInputComponentProps<T>) => (
    <CreateItemInput
      id={id}
      onBeforeItemCreate={onBeforeItemCreate}
      onAfterItemCreated={onAfterItemCreated}
      viewMode={viewMode}
      doc={doc}
      documentItemTypes={documentItemTypes}
      item={parentItem}
      isRootItem={isRootItem}
      itemChildren={itemChildren}
      nodeRank={nodeRank}
      autoFocus={false}
      createItem={createItem}
      createItemByType={createItemByType}
      createItemFromTemplate={createItemFromTemplate}
      createItemsFromTemplates={createItemsFromTemplates}
      createItemsWithProducts={createItemsWithProducts}
      pasteItem={pasteItem}
      isAllowedToPasteItem={isAllowedToPasteItem}
    />
  );

  DocDataGridInputCell.displayName = "DocDataGridInputCell";
  return DocDataGridInputCell;
}
